const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const nonNegative = (value) => Math.max(0, toNumber(value));

const applyCap = (amount, cap) => {
  if (cap === null || cap === undefined) {
    return amount;
  }

  return Math.min(amount, nonNegative(cap));
};

const roundCurrency = (value) => Math.round(value);

const getMarketplaceRules = (rules, marketplace) => {
  const marketplaces = rules?.marketplaces || {};
  return marketplaces[marketplace] || marketplaces.shopee || Object.values(marketplaces)[0] || {};
};

const getCategoryRules = (marketplaceRules, category) => {
  const categories = marketplaceRules?.categories || {};
  return categories[category] || categories.general || Object.values(categories)[0] || {};
};

const getProcessingFee = (marketplaceRules, quantity) => {
  const processingFee = marketplaceRules?.orderProcessingFee || {};
  const amount = nonNegative(processingFee.amount);

  if (processingFee.mode === 'per_item') {
    return amount * quantity;
  }

  return amount;
};

const getSelectedPrograms = (input, marketplaceRules) => {
  const programs = marketplaceRules?.programs || {};

  return Object.entries(programs).filter(([programKey]) => {
    return Boolean(input[programKey]);
  });
};

const getMarketplaceVariableRate = (input, marketplaceRules, categoryRules) => {
  const adminRate = nonNegative(categoryRules.adminFeeRate);
  const selectedProgramRate = getSelectedPrograms(input, marketplaceRules).reduce(
    (total, [, program]) => total + nonNegative(program.rate),
    0,
  );
  const affiliateRate = marketplaceRules?.affiliate?.enabled
    ? clamp(nonNegative(input.affiliateRate) / 100, 0, 1)
    : 0;
  const adsRate = marketplaceRules?.ads?.enabled ? clamp(nonNegative(input.adsRate) / 100, 0, 1) : 0;

  return adminRate + selectedProgramRate + affiliateRate + adsRate;
};

const calculateForUnitPrice = (input, rules, unitPrice) => {
  const marketplaceRules = getMarketplaceRules(rules, input.marketplace);
  const categoryRules = getCategoryRules(marketplaceRules, input.category);

  const quantity = Math.max(1, Math.floor(nonNegative(input.quantity) || 1));
  const sellingPrice = nonNegative(unitPrice);
  const costOfGoods = nonNegative(input.costOfGoods);
  const sellerDiscount = nonNegative(input.sellerDiscount);
  const sellerVoucher = nonNegative(input.sellerVoucher);
  const packingCost = nonNegative(input.packingCost);

  const grossSales = sellingPrice * quantity;
  const totalSellerDiscount = Math.min(grossSales, sellerDiscount + sellerVoucher);
  const netProductValue = Math.max(0, grossSales - totalSellerDiscount);

  const adminFee = applyCap(netProductValue * nonNegative(categoryRules.adminFeeRate), categoryRules.adminFeeCap);

  const programDetails = getSelectedPrograms(input, marketplaceRules).map(([programKey, program]) => ({
    key: programKey,
    label: program.label || programKey,
    amount: applyCap(netProductValue * nonNegative(program.rate), program.cap),
  }));

  const programFee = programDetails.reduce((total, program) => total + program.amount, 0);

  const affiliateRate = marketplaceRules?.affiliate?.enabled
    ? clamp(nonNegative(input.affiliateRate) / 100, 0, 1)
    : 0;
  const adsRate = marketplaceRules?.ads?.enabled ? clamp(nonNegative(input.adsRate) / 100, 0, 1) : 0;
  const affiliateFee = applyCap(netProductValue * affiliateRate, marketplaceRules?.affiliate?.cap);
  const adsFee = applyCap(netProductValue * adsRate, marketplaceRules?.ads?.cap);
  const orderProcessingFee = getProcessingFee(marketplaceRules, quantity);

  const totalMarketplaceFees = adminFee + programFee + affiliateFee + adsFee + orderProcessingFee;
  const totalHpp = costOfGoods * quantity;
  const totalPackingCost = packingCost;
  const netPayout = netProductValue - totalMarketplaceFees;
  const netProfit = netPayout - totalHpp - totalPackingCost;
  const marginPercentage = netProductValue > 0 ? (netProfit / netProductValue) * 100 : 0;

  return {
    marketplaceLabel: marketplaceRules.label || input.marketplace,
    categoryLabel: rules?.categories?.[input.category]?.label || input.category,
    quantity,
    sellingPrice,
    grossSales,
    totalSellerDiscount,
    netProductValue,
    adminFee,
    programDetails,
    programFee,
    affiliateFee,
    adsFee,
    orderProcessingFee,
    totalMarketplaceFees,
    totalHpp,
    totalPackingCost,
    netPayout,
    netProfit,
    marginPercentage,
  };
};

const getProfitStatus = (netProfit, marginPercentage, rules) => {
  const thresholds = rules?.statusThresholds || {};
  const thinBelow = toNumber(thresholds.thinMarginBelow) || 5;
  const safeBelow = toNumber(thresholds.safeMarginBelow) || 15;

  if (netProfit < 0) {
    return 'Loss';
  }

  if (marginPercentage < thinBelow) {
    return 'Thin';
  }

  if (marginPercentage < safeBelow) {
    return 'Safe';
  }

  return 'Good';
};

const findSafeSellingPrice = (input, rules) => {
  const targetProfit = nonNegative(input.targetProfit);
  const currentPrice = nonNegative(input.sellingPrice);
  const quantity = Math.max(1, Math.floor(nonNegative(input.quantity) || 1));
  const costBase = nonNegative(input.costOfGoods) * quantity + nonNegative(input.packingCost);
  const marketplaceRules = getMarketplaceRules(rules, input.marketplace);
  const categoryRules = getCategoryRules(marketplaceRules, input.category);
  const variableRate = getMarketplaceVariableRate(input, marketplaceRules, categoryRules);
  const discountBase = nonNegative(input.sellerDiscount) + nonNegative(input.sellerVoucher);
  const estimatedUnitPrice = (costBase + targetProfit + discountBase) / Math.max(0.01, quantity * (1 - variableRate));

  let low = 0;
  let high = Math.max(currentPrice, estimatedUnitPrice, 1);

  for (let step = 0; step < 30; step += 1) {
    const profitAtHigh = calculateForUnitPrice(input, rules, high).netProfit;

    if (profitAtHigh >= targetProfit) {
      break;
    }

    high *= 2;
  }

  for (let step = 0; step < 40; step += 1) {
    const middle = (low + high) / 2;
    const profitAtMiddle = calculateForUnitPrice(input, rules, middle).netProfit;

    if (profitAtMiddle >= targetProfit) {
      high = middle;
    } else {
      low = middle;
    }
  }

  return Math.ceil(high);
};

export function calculateFees(input, rules) {
  const breakdown = calculateForUnitPrice(input, rules, input.sellingPrice);
  const safeSellingPrice = findSafeSellingPrice(input, rules);
  const profitStatus = getProfitStatus(breakdown.netProfit, breakdown.marginPercentage, rules);

  return {
    ...Object.fromEntries(
      Object.entries(breakdown).map(([key, value]) => [
        key,
        typeof value === 'number' ? roundCurrency(value) : value,
      ]),
    ),
    safeSellingPrice,
    profitStatus,
  };
}
